import { isMismatch as uxTreadIsMismatch, readGeolocCookie } from '@nike/ux-tread-geoloc';
import {
  getConsumerChoice as uxTreadGetConsumerChoice,
  setLocale as uxTreadSetLocale,
} from '@nike/ux-tread-locale-settings';
import localizationStrings from '../client/data/localizationStrings.json';
import webShellLocMappings from '../client/data/webShellLocMappings.json';

/**
 * @returns {{choice?: string; chosen?: boolean}} - consumer choice settings
 * (e.g. { choice: 'gb/en_gb', chosen: true })
 */
export function getConsumerChoice() {
  try {
    // getConsumerChoice can throw if the cookie is invalid
    return uxTreadGetConsumerChoice();
  } catch {
    return {};
  }
}

/**
 * Access and compare the 3 values that help us decide where a user should be shopping.
 * @param {string} country
 * @returns {boolean} if true, conflicting values were discovered and we'll likely want to ask the user for additional input
 */
function isMismatch(country) {
  try {
    // isMismatch calls getConsumerChoice internally and can throw if cookie invalid
    return uxTreadIsMismatch(country);
  } catch {
    return false;
  }
}

/**
 * Return the geoloc cookie country value.
 * @returns {String|null}
 */
export function getGeolocCountry() {
  const { country } = readGeolocCookie() || { country: null };
  return country ? country.toLowerCase() : null;
}

/**
 * Get the geo-mismatched country value.
 * @param country {String}
 * @param fallback {String|null} country to fallback if no mismatch
 * @returns {String|null}
 * @throws Error if validation of cookie fails
 */
export function getGeoMismatchCountry(country, fallback = null) {
  // Disable this object if no mismatch is happening.
  if (!isMismatch(country)) {
    return fallback;
  }
  // The cookie can fail validation and throw here.
  const { choice } = getConsumerChoice();

  const chosenMarketplace = choice && choice.slice(0, 2).toLowerCase();
  if (country.toLowerCase() === chosenMarketplace) {
    return fallback;
  }
  // If no consumer choice cookie set, defer to geoloc country.
  const mismatchedCountry = choice ? chosenMarketplace : getGeolocCountry();

  // Disable this object if mismatched but value doesn't exist.
  if (!mismatchedCountry) {
    return fallback;
  }
  // At this point, the mismatched country could still not be supported.
  return mismatchedCountry.toLowerCase();
}

/**
 * @param {string} marketplace - currently requires a country in ISO 3166-1 alpha-2 format
 * @param {string} language - currently requires a language in legacy format like fr_FR
 * @param {boolean} chosen - if true, sets consumer choice (which is never unset)
 * @returns {boolean} - indication of successfully set locale
 */
export function setLocale(marketplace, language, chosen) {
  return uxTreadSetLocale(marketplace, language, chosen);
}

/**
 * Evaluates the the users consumerChoice cookie to see if they have a
 * preferred localized language and returns that or null if non exists.
 * @returns {string|null}
 */
export function consumerChoiceLanguage() {
  const consumerChoice = getConsumerChoice().choice;
  if (!consumerChoice) return null;
  // For certain language lookups, locale is included
  // Check the lang-locale combo first, then default to just language
  const [, consumerLang, consumerLocale] = consumerChoice.split(/\/|_/);
  const langLocale =
    `${consumerLang}-${consumerLocale.toUpperCase()}` !== 'es-LA'
      ? `${consumerLang}-${consumerLocale.toUpperCase()}`
      : 'es-419';

  const consumerChoiceLang = localizationStrings[langLocale] ? langLocale : consumerLang;
  return localizationStrings[consumerChoiceLang] ? consumerChoiceLang : null;
}

/**
 * Determines if the mismatched country is supported or not.
 * If mismatchedCountry is falsy it will return false.
 * @returns {boolean}
 */
export const isSupportedCountry = mismatchedCountry => {
  const supportedCountries = Object.keys(webShellLocMappings);
  return !!mismatchedCountry && supportedCountries.includes(mismatchedCountry);
};
